/**
 * Capture-date labeling for Site Inspection.
 *
 * Street View imagery is a point-in-time snapshot, so the capture date is what
 * dates every signage or occupancy read an operator makes from it. This is the
 * one caveat surfaced in the bulletin.
 */

export type SiteEvidenceFlagSeverity = 'info' | 'warning';

export type SiteEvidenceFlag = {
  id: string;
  severity: SiteEvidenceFlagSeverity;
  title: string;
  detail: string;
};

/** Treat imagery older than this as stale enough to warn (years). */
export const SITE_IMAGE_STALE_YEARS = 2;

export function parseStreetViewImageDate(raw: string | null | undefined): {
  label: string;
  year: number | null;
} | null {
  const trimmed = (raw ?? '').trim();
  if (!trimmed) return null;
  // Google commonly returns "YYYY-MM" or "YYYY".
  const match = trimmed.match(/^(\d{4})(?:-(\d{1,2}))?/);
  if (!match) return { label: trimmed, year: null };
  const year = Number(match[1]);
  const month = match[2] ? Number(match[2]) : null;
  const label =
    month != null && month >= 1 && month <= 12
      ? `${match[1]}-${String(month).padStart(2, '0')}`
      : match[1]!;
  return { label, year: Number.isFinite(year) ? year : null };
}

export function isStaleImageYear(
  year: number | null | undefined,
  nowYear = new Date().getFullYear(),
  staleAfterYears = SITE_IMAGE_STALE_YEARS,
): boolean {
  if (year == null || !Number.isFinite(year)) return false;
  return nowYear - year >= staleAfterYears;
}

export function buildImageryDateFlag(input: {
  imageDateRaw?: string | null;
  nowYear?: number;
}): SiteEvidenceFlag {
  const parsed = parseStreetViewImageDate(input.imageDateRaw);
  if (!parsed) {
    return {
      id: 'imagery-date-unknown',
      severity: 'warning',
      title: 'Street View capture date unknown',
      detail:
        'Without a capture date, do not assume the façade reflects today’s tenant. Imagery is a point-in-time snapshot.',
    };
  }

  const stale = isStaleImageYear(parsed.year, input.nowYear);
  return {
    id: 'imagery-date',
    severity: stale ? 'warning' : 'info',
    title: stale
      ? `Street View imagery dated ${parsed.label} (may be stale)`
      : `Street View imagery dated ${parsed.label}`,
    detail:
      'Use the date when judging signage and occupancy. Imagery is a point-in-time snapshot.',
  };
}
